import { Spinner } from "components/Spinner";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDoubleLeft,
  ChevronDoubleRight,
  ChevronDown,
} from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";

import { PAGINATION_OPTIONS, TABLE_PAGINATION_ROWS_PER_PAGE_SELECT_ID } from "./constants";
import styles from "./styles.module.css";

type TablePaginationProps = {
  page: number;
  rowsPerPage: number;
  setRowsPerPage: (rows: number) => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  goToLastPage: () => void;
  goToFirstPage: () => void;
  from: number;
  to: number;
  lastPage: number;
  itemsCount: number;
  loading: boolean;
  paginationOptions?: number[];
};

const TablePagination = ({
  page,
  rowsPerPage,
  setRowsPerPage,
  goToNextPage,
  goToPrevPage,
  goToLastPage,
  goToFirstPage,
  from,
  to,
  lastPage,
  itemsCount,
  loading,
  paginationOptions = PAGINATION_OPTIONS,
}: TablePaginationProps) => {
  const isFirstPage = page === 1;
  const isLastPage = page === lastPage;
  const showRange = Boolean(from && to && itemsCount);

  return (
    <Box
      className={styles.paginationContainer}
      align="center"
      justify="between"
      padding="medium large"
    >
      <Box align="center" gap="medium" className={styles.rowsPerPage}>
        <Typography variant="p-t7" tag="span" color="secondary">
          Rows per page:
        </Typography>
        <DropdownMenu
          triggerComponent={
            <Button
              id={TABLE_PAGINATION_ROWS_PER_PAGE_SELECT_ID}
              size="small"
              variant="secondary"
              endIcon={ChevronDown}
            >
              {String(rowsPerPage)}
            </Button>
          }
        >
          {paginationOptions.map((value, i) => (
            <DropdownMenuItem key={i} onAction={() => setRowsPerPage(value)}>
              {value}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </Box>

      <Box align="center" gap="large" className={styles.paginationControls}>
        {loading && <Icon src={Spinner} />}
        {showRange && (
          <Typography variant="p-t7" tag="span" color="secondary">
            {from} - {to} of {itemsCount}
          </Typography>
        )}
        <Box align="center" gap="medium">
          <ButtonIcon
            onPress={goToFirstPage}
            disabled={isFirstPage || loading}
            className={styles.paginationButton}
            variant="secondary"
            icon={ChevronDoubleLeft}
          >
            Go to first page
          </ButtonIcon>

          <Tooltip
            on={(tooltipProps) => (
              <Button
                {...tooltipProps}
                onPress={goToPrevPage}
                disabled={isFirstPage || loading}
                size="small"
                variant="secondary"
                aria-label="Go to previous page"
                startIcon={ArrowLeft}
              >
                Prev
              </Button>
            )}
            placement="top"
          >
            Go to previous page
          </Tooltip>
          <Tooltip
            on={(tooltipProps) => (
              <Button
                {...tooltipProps}
                onPress={goToNextPage}
                disabled={isLastPage || loading}
                size="small"
                variant="secondary"
                aria-label="Go to next page"
                endIcon={ArrowRight}
              >
                Next
              </Button>
            )}
            placement="top"
          >
            Go to next page
          </Tooltip>
          {/* TODO: implement this button when backend provided */}
          <ButtonIcon
            onPress={goToLastPage}
            disabled={isLastPage || loading}
            className={styles.paginationButton}
            variant="secondary"
            icon={ChevronDoubleRight}
          >
            Go to last page
          </ButtonIcon>
        </Box>
      </Box>
    </Box>
  );
};

export default TablePagination;
